import React, { useState, useEffect, useCallback } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { FaTrashAlt, FaLock } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import ScaleLoader from "react-spinners/ScaleLoader";
import ApiConfig from "../../api/ApiConfig";
import { simplePostCall } from "../../api/ApiServices";
import { financeAuthPayload } from "../../components/finance/AccountSelect";
import ConfirmModal from "../../components/ConfirmModal";
import "../../Styles/yellowcalfcss/Finance.css";

/**
 * Period Lock (Phase D)
 *
 * Locking a date range blocks any journal from being posted or modified within
 * it — the books for a closed month can't be tampered with. Deleting a lock
 * re-opens the period.
 *
 * Backend: period-lock/list, period-lock/create, period-lock/delete.
 * Row shape: { period_lock_id, lock_from_date, lock_to_date, lock_reason,
 *   locked_by, created_at }
 */
const PeriodLock = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({
    lock_from_date: "",
    lock_to_date: "",
    lock_reason: "",
  });
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchLocks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await simplePostCall(
        ApiConfig.periodLockList,
        JSON.stringify(financeAuthPayload())
      );
      setRows(res?.result && Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Period lock list failed:", err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLocks();
  }, [fetchLocks]);

  const openCreate = () => {
    setForm({ lock_from_date: "", lock_to_date: "", lock_reason: "" });
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const submit = async () => {
    if (!form.lock_from_date || !form.lock_to_date) {
      toast.warning("Both the from and to dates are required.");
      return;
    }
    if (form.lock_from_date > form.lock_to_date) {
      toast.warning("The from date must be on or before the to date.");
      return;
    }
    setSaving(true);
    try {
      const res = await simplePostCall(
        ApiConfig.periodLockCreate,
        JSON.stringify({
          ...financeAuthPayload(),
          lock_from_date: form.lock_from_date,
          lock_to_date: form.lock_to_date,
          lock_reason: form.lock_reason,
        })
      );
      if (res?.result) {
        toast.success("Period locked.");
        setShowModal(false);
        fetchLocks();
      } else {
        toast.error(res?.message || "Failed to lock period.");
      }
    } catch (err) {
      toast.error("An error occurred while locking the period.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await simplePostCall(
        ApiConfig.periodLockDelete,
        JSON.stringify({
          ...financeAuthPayload(),
          period_lock_id: deleteTarget.period_lock_id,
        })
      );
      if (res?.result) {
        toast.success("Period unlocked.");
        fetchLocks();
      } else {
        toast.error(res?.message || "Failed to unlock period.");
      }
    } catch (err) {
      toast.error("An error occurred while unlocking.");
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const fmt = (d) => (d ? new Date(d).toLocaleDateString() : "—");

  return (
    <div className="finance-page-container">
      <ToastContainer />

      <div className="special-header panel-white-all">
        <h4 className="page-title">Period Lock</h4>
        <div className="action-buttons-group">
          <Button className="btn btn-primary" onClick={openCreate}>
            <FaLock className="me-1" /> Lock Period
          </Button>
        </div>
      </div>

      <div className="yc-list-body">
        <div className="yc-list-thead-card">
          <div className="yc-list-grid yc-list-grid-5">
            <div className="yc-list-cell">Sr.No</div>
            <div className="yc-list-cell">From</div>
            <div className="yc-list-cell">To</div>
            <div className="yc-list-cell">Reason</div>
            <div className="yc-list-cell">Action</div>
          </div>
        </div>

        {loading ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ height: "20vh" }}
          >
            <ScaleLoader color="#F4BB44" />
          </div>
        ) : rows.length === 0 ? (
          <div className="fin-empty">
            <span className="fin-empty-title">No locked periods</span>
            <span className="fin-empty-text">
              Lock a date range once its books are closed to prevent any further
              journal postings or edits within it.
            </span>
            <Button className="btn btn-primary mt-2" onClick={openCreate}>
              Lock a Period
            </Button>
          </div>
        ) : (
          <div className="yc-list-row-list">
            {rows.map((r, idx) => (
              <div key={r.period_lock_id || idx} className="yc-list-row-card">
                <div className="yc-list-grid yc-list-grid-5">
                  <div className="yc-list-cell">{idx + 1}</div>
                  <div className="yc-list-cell fw-semibold">
                    {fmt(r.lock_from_date)}
                  </div>
                  <div className="yc-list-cell fw-semibold">
                    {fmt(r.lock_to_date)}
                  </div>
                  <div className="yc-list-cell">{r.lock_reason || "—"}</div>
                  <div className="yc-list-cell yc-list-cell-actions">
                    <button
                      className="yc-action-icon yc-action-icon--danger"
                      title="Unlock period"
                      onClick={() => setDeleteTarget(r)}
                    >
                      <FaTrashAlt />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Lock period modal */}
      <Modal show={showModal} onHide={() => !saving && setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Lock Period</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="fin-inline-warning">
            While locked, no journal entry can be posted or edited with a date in
            this range. You can unlock it later.
          </div>
          <Form.Group className="mb-3">
            <Form.Label>
              From Date <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="date"
              name="lock_from_date"
              value={form.lock_from_date}
              onChange={handleChange}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>
              To Date <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="date"
              name="lock_to_date"
              value={form.lock_to_date}
              onChange={handleChange}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Reason</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              name="lock_reason"
              value={form.lock_reason}
              onChange={handleChange}
              placeholder="e.g. May books closed and reconciled"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowModal(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button className="btn btn-primary" onClick={submit} disabled={saving}>
            {saving ? <Spinner size="sm" /> : "Lock Period"}
          </Button>
        </Modal.Footer>
      </Modal>

      <ConfirmModal
        show={!!deleteTarget}
        onHide={() => setDeleteTarget(null)}
        title="Unlock Period"
        message={
          deleteTarget
            ? `Unlock ${fmt(deleteTarget.lock_from_date)} – ${fmt(
                deleteTarget.lock_to_date
              )}? Journals in this range will become editable again.`
            : ""
        }
        confirmText="Unlock"
        confirmVariant="warning"
        onConfirm={handleDelete}
        isLoading={deleting}
      />
    </div>
  );
};

export default PeriodLock;
